import { alpha, createTheme, type ThemeOptions } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import type {} from "@mui/x-data-grid/themeAugmentation";
import {
	primaryMain,
	primaryLight,
	backgroundDefault,
	backgroundPaper,
	backgroundDefaultDark,
	backgroundPaperDark,
	textPrimary,
	textSecondary,
	textDisabled,
	textPrimaryDark,
	textSecondaryDark,
	textDisabledDark,
	errorMain,
	errorLight,
	warningMain,
	warningLight,
	successMain,
	successLight,
	infoMain,
	packsizeGreenMain,
	packsizeDeepGreenMain,
	packsizeNavyMain,
	packsizeDeepGrayMain,
	packsizeBlueMain,
	packsizeDeepBlueMain,
	packsizeRustMain,
	packsizeGreenGray,
	packsizeGrayMain,
} from "./palettes";
import type {} from "./types/mui-table";
import type {} from "./types/packsize";

const { palette: basePalette } = createTheme();
import type {} from "./types/mui-button";
import type {} from "./types/mui-chip";
const { augmentColor } = basePalette;

export const themeOptions: ThemeOptions = {
	colorSchemes: {
		light: {
			palette: {
				primary: {
					main: primaryMain,
					light: primaryLight,
				},
				secondary: {
					main: primaryMain,
				},
				background: {
					default: backgroundDefault,
					paper: backgroundPaper,
				},
				text: {
					primary: textPrimary,
					secondary: textSecondary,
					disabled: textDisabled,
				},
				error: {
					main: errorMain,
					light: errorLight,
				},
				warning: {
					main: warningMain,
					light: warningLight,
				},
				success: {
					main: successMain,
					light: successLight,
				},
				info: {
					main: infoMain,
				},
				brandGreen: augmentColor({ color: { main: packsizeGreenMain, contrastText: textPrimary } }),
				brandDeepGreen: augmentColor({ color: { main: packsizeDeepGreenMain, contrastText: textPrimary } }),
				brandNavy: augmentColor({ color: { main: packsizeNavyMain, contrastText: textPrimary } }),
				brandDeepGray: augmentColor({ color: { main: packsizeDeepGrayMain, contrastText: textPrimary } }),
				brandBlue: augmentColor({ color: { main: packsizeBlueMain, contrastText: textPrimary } }),
				brandDeepBlue: augmentColor({ color: { main: packsizeDeepBlueMain, contrastText: textPrimary } }),
				brandRust: augmentColor({ color: { main: packsizeRustMain, contrastText: textPrimary } }),
				brandGreenGray: augmentColor({ color: { main: packsizeGreenGray, contrastText: textPrimary } }),
				brandGray: augmentColor({ color: { main: packsizeGrayMain, contrastText: textPrimary } }),
			},
		},
		dark: {
			palette: {
				primary: {
					main: primaryMain,
					light: primaryLight,
				},
				secondary: {
					main: primaryMain,
				},
				background: {
					default: backgroundDefaultDark,
					paper: backgroundPaperDark,
				},
				text: {
					primary: textPrimaryDark,
					secondary: textSecondaryDark,
					disabled: textDisabledDark,
				},
				error: {
					main: errorMain,
				},
				warning: {
					main: warningMain,
				},
				success: {
					main: successMain,
				},
				info: {
					main: infoMain,
				},
				brandGreen: augmentColor({ color: { main: packsizeGreenMain, contrastText: textPrimary } }),
				brandDeepGreen: augmentColor({ color: { main: packsizeDeepGreenMain, contrastText: textPrimaryDark } }),
				brandNavy: augmentColor({ color: { main: packsizeNavyMain, contrastText: textPrimaryDark } }),
				brandDeepGray: augmentColor({ color: { main: packsizeDeepGrayMain, contrastText: textPrimaryDark } }),
				brandBlue: augmentColor({ color: { main: packsizeBlueMain, contrastText: textPrimary } }),
				brandDeepBlue: augmentColor({ color: { main: packsizeDeepBlueMain, contrastText: textPrimary } }),
				brandRust: augmentColor({ color: { main: packsizeRustMain, contrastText: textPrimaryDark } }),
				brandGreenGray: augmentColor({ color: { main: packsizeGreenGray, contrastText: textPrimary } }),
				brandGray: augmentColor({ color: { main: packsizeGrayMain, contrastText: textPrimary } }),
			},
		},
	},
	typography: {
		fontFamily: "Gellix,Stolzl,Helvetica Neue,Arial,Helvetica,sans-serif",
		h1: {
			fontSize: 32,
			fontWeight: 700,
		},
		h2: {
			fontSize: 24,
			fontWeight: 700,
		},
		h3: {
			fontSize: 20,
			fontWeight: 700,
		},
		h4: {
			fontSize: 16,
			fontWeight: 700,
		},
		h5: {
			fontSize: 14,
			fontWeight: 700,
		},
		h6: {
			fontSize: 14,
			fontWeight: 700,
		},
		subtitle1: {
			fontSize: 14,
			fontWeight: 600,
		},
		subtitle2: {
			fontSize: 12,
			fontWeight: 400,
		},
		fontSize: 16,
		body1: {
			fontSize: 16,
			fontWeight: 600,
		},
		body2: {
			fontSize: 14,
			fontWeight: 600,
		},
		button: {
			fontSize: 16,
			fontWeight: 700,
			textTransform: "capitalize",
		},
		caption: {
			fontSize: 12,
			fontWeight: 400,
		},
	},
	shape: {
		borderRadius: 8,
	},
	components: {
		MuiCssBaseline: {
			defaultProps: {
				enableColorScheme: true,
			},
		},
		MuiButtonBase: {
			defaultProps: {
				disableRipple: true,
				style: {
					textTransform: "capitalize",
				},
			},
		},
		MuiButton: {
			defaultProps: {
				variant: "contained",
				disableElevation: true,
				disableRipple: true,
			},
		},
		MuiCheckbox: {
			defaultProps: {
				disableRipple: true,
			},
		},
		MuiButtonGroup: {
			defaultProps: {
				disableRipple: true,
				disableElevation: true,
			},
		},
		MuiPaper: {
			defaultProps: {
				variant: "outlined",
			},
		},
		MuiCard: {
			defaultProps: {
				variant: "outlined",
			},
		},
		MuiCardActions: {
			styleOverrides: {
				root: {
					paddingLeft: 16,
					paddingRight: 16,
					paddingBottom: 16,
				},
			},
		},
		MuiAccordion: {
			defaultProps: {
				variant: "outlined",
				disableGutters: true,
			},
			styleOverrides: {
				root: {
					"&:not(:last-child)": {
						borderBottom: 0,
					},
					"&::before": {
						display: "none",
					},
				},
			},
		},
		MuiSwitch: {
			styleOverrides: {
				root: {
					padding: 8,
					minHeight: 44,
					height: 44,
					minWidth: 58,
					width: 58,
				},
				switchBase: {
					padding: 12,
					"&.Mui-checked": {
						transform: "translateX(14px)",
					},
				},
				thumb: ({ theme }) => ({
					backgroundColor: theme.palette.background.paper,
					width: 20,
					height: 20,
					boxShadow: "unset",
				}),
				track: {
					borderRadius: 100,
					minHeight: 24,
				},
			},
		},
		MuiTableCell: {
			variants: [
				{
					props: { variant: "head" },
					style: {
						fontWeight: 700,
					},
				},
			],
			styleOverrides: {
				root: ({ theme }) => ({
					"&.MuiTableCell-head": {
						"&.MuiTableCell-stickyHeader": {
							backgroundColor: theme.palette.background.paper,
						},
					},
				}),
			},
		},
		MuiTableBody: {
			variants: [
				{
					props: { variant: "striped" },
					style: ({ theme }) => ({
						"& .MuiTableRow-root:nth-of-type(odd)": {
							backgroundColor: theme.palette.action.hover,
						},
						"& .MuiTableRow-root.MuiTableRow-hover:hover": {
							backgroundColor: alpha(theme.palette.action.hover, 0.08),
						},
					}),
				},
			],
		},
		MuiTablePagination: {
			defaultProps: {
				slotProps: {
					select: {
						IconComponent: ({ sx, ...props }) => <KeyboardArrowDownIcon {...props} sx={{ fontSize: 24, ...sx }} />,
					},
				},
			},
			styleOverrides: {
				root: ({ theme }) => ({
					borderTop: `1px solid ${theme.palette.divider}`,
				}),
			},
		},
		MuiAutocomplete: {
			defaultProps: {
				popupIcon: <KeyboardArrowDownIcon />,
			},
		},
		MuiSelect: {
			defaultProps: {
				IconComponent: KeyboardArrowDownIcon,
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					backgroundColor: packsizeNavyMain,
					boxShadow: "none",
					border: "none",
				},
			},
		},
		MuiDataGrid: {
			styleOverrides: {
				cell: {
					"&:focus": {
						outline: "none",
					},
					"&:focus-within": {
						outline: "none",
					},
				},
				columnHeader: {
					"&:focus": {
						outline: "none",
					},
					"&:focus-within": {
						outline: "none",
					},
				},
			},
		},
	},
};

export const theme = createTheme(themeOptions);
